import axios from "axios";
import moment from "moment";
import { getRoundedValue } from "../../../../helpers/mealsHelper";
import i18n from "../../../../i18n";
import { DateFormat } from "../../../../modules/constants";
import { getClaims } from "../../../../modules/login";
import {
  orderType,
  tablePageSizes,
} from "../../../ui-library/CustomTable/constants";

// Constants
const LOADING_TABLE_DATA = "LOADING_TABLE_DATA";
const SET_TABLE_DATA = "SET_TABLE_DATA";
const RESET_STATE = "RESET_STATE";
const LOGOUT = "LOGOUT";
const SET_DIET_DIARY_MENU_ITEMS = "SET_DIET_DIARY_MENU_ITEMS";

//Acessors
const getTotalItems = (state) => state?.totalItems;
const getEntries = (state) => state?.entries;
const getSearchedParams = (state) => state?.searchedParams;
const getLoading = (state) => state?.loading;
const getDietMenuItems = (state) => state?.dietMenuItems;

const canCreateEntriesMealLog = (state) =>
  getClaims(state, "mealLog.entries.canCreate");
const canEditEntriesMealLog = (state) =>
  getClaims(state, "mealLog.entries.canEdit");
const canViewDetailMealLog = (state) =>
  getClaims(state, "mealLog.detail.canView");

// Default State
const defaultState = {
  entries: [],
  totalItems: null,
  loading: false,
  dietMenuItems: null,
  searchedParams: {
    page: 0,
    limit: tablePageSizes[3],
    search: "",
    orderBy: "created",
    order: orderType.DESC,
  },
};

//Reducer
export const studyEntriesTableReducer = (state = defaultState, action) => {
  switch (action.type) {
    case LOADING_TABLE_DATA:
      return {
        ...state,
        loading: true,
      };
    case SET_TABLE_DATA: {
      const { tableData, searchedParams } = action;
      return Object.assign({}, state, {
        entries: tableData.total,
        totalItems: tableData.totalEntries,
        searchedParams: searchedParams,
        loading: false,
      });
    }
    case SET_DIET_DIARY_MENU_ITEMS: {
      const { dietMenuItems } = action;

      return Object.assign({}, state, {
        dietMenuItems: dietMenuItems,
      });
    }
    case RESET_STATE:
      return defaultState;
    case LOGOUT:
      return defaultState;
    default:
      return state;
  }
};

const fetchEntriesTableData =
  (searchedParams, startDate, endDate, participantId = null) =>
  (dispatch) => {
    const params = {
      ...searchedParams,
      StartDate: startDate,
      EndDate: endDate,
    };

    if (participantId) {
      params.ParticipantId = participantId;
    }

    dispatch({
      type: LOADING_TABLE_DATA,
    });

    return axios
      .get(`/api/diet/userinfo/entries`, { params })
      .then(({ data: { entries } }) => {
        dispatch({
          type: SET_TABLE_DATA,
          tableData: parseTableData(entries),
          searchedParams: searchedParams,
        });
        return Promise.resolve("Got data latest meal data for you.");
      })
      .catch(() => {
        return Promise.reject(i18n.t("errorText.dietDiary.entries.get"));
      });
  };

const parseTableData = (data) => {
  const { total, totalEntries } = data;
  let formattedData = [];
  if (total && totalEntries) {
    total.forEach((entry) => {
      const {
        mealDate,
        mealJson,
        key,
        completed,
        reason,
        reasonForChange,
        recall,
        hceMceEvent,
      } = entry;
      const mDate = moment(mealDate).format(DateFormat.EXTENDED_FORMAT);
      const meal = JSON.parse(mealJson);

      const mealObj = {
        time: moment(mealDate).format("h:mm A"),
        description: getMealDescription(meal),
        calories: { ...meal.aboutMeal.Calories },
        mediumChainFats: { ...meal.aboutMeal.MediumChainFats },
        longChainFats: { ...meal.aboutMeal.LongChainFats },
        fats: { ...meal.aboutMeal.Fats },
        isCompleted: completed,
        is24hRecall: recall,
        hceMceEvent: hceMceEvent,
        json: mealJson,
        dateTime: mealDate,
        mealId: key,
        reason: reason,
        reasonForChange: reasonForChange,
      };

      const dayObj = {
        date: mDate,
      };
      //Check if an object with this date already exists
      const dateAtIndex = formattedData.findIndex((d) => d.date === mDate);

      if (dateAtIndex >= 0) {
        //Add this meal if object exists
        formattedData[dateAtIndex].meals.push(mealObj);
        //Update day values with all existing meals!
        let mealDaySum = mealsToDay(formattedData[dateAtIndex].meals);
        formattedData[dateAtIndex].calories = mealDaySum.calories;
        formattedData[dateAtIndex].mediumChainFats = mealDaySum.mediumChainFats;
        formattedData[dateAtIndex].longChainFats = mealDaySum.longChainFats;
        formattedData[dateAtIndex].fats = mealDaySum.fats;
      } else {
        //This is the only meal
        dayObj.meals = [mealObj];
        //Day's value are of this meal only
        dayObj.calories = mealObj.calories;
        dayObj.mediumChainFats = mealObj.mediumChainFats;
        dayObj.longChainFats = mealObj.longChainFats;
        dayObj.fats = mealObj.fats;
        formattedData.push(dayObj);
      }
    });
  }

  return { total: formattedData, totalEntries };
};

const getMealDescription = (meal) => {
  const itemTitles = meal.items.map((item, index) => {
    return index < 2 ? item.title : null;
  });

  let description = itemTitles.filter((title) => title !== null).join(", ");

  if (meal.items.length > 2) {
    description += "...";
  }

  return description;
};

const mealsToDay = (array = []) => {
  const tempData = array.reduce((p, c) => {
    return {
      calories: {
        value: getRoundedValue(
          Number(p.calories.value ?? 0) + Number(c.calories.value ?? 0)
        ),
        unit: c.calories.unit ? c.calories.unit : p.calories.unit,
      },
      mediumChainFats: {
        value: getRoundedValue(
          Number(p.mediumChainFats.value ?? 0) +
            Number(c.mediumChainFats.value ?? 0)
        ),
        unit: c.mediumChainFats.unit
          ? c.mediumChainFats.unit
          : p.mediumChainFats.unit,
      },
      longChainFats: {
        value: getRoundedValue(
          Number(p.longChainFats.value ?? 0) +
            Number(c.longChainFats.value ?? 0)
        ),
        unit: c.longChainFats.unit
          ? c.longChainFats.unit
          : p.longChainFats.unit,
      },
      fats: {
        percentage: p.fats.percentage + c.fats.percentage,
        value: getRoundedValue(
          Number(p.fats.value ?? 0) + Number(c.fats.value ?? 0)
        ),
        unit: c.fats.unit ? c.fats.unit : p.fats.unit,
      },
    };
  });

  return tempData;
};

const resetState = () => (dispatch) => {
  dispatch({ type: RESET_STATE });
};

const fetchDietDiaryMenuItems = () => (dispatch) => {
  return axios.get(`/api/dietmenu`).then(({ data }) => {
    dispatch({ type: SET_DIET_DIARY_MENU_ITEMS, dietMenuItems: data });
  });
};

// Maps
export const mapDispatchToProps = {
  fetchEntriesTableData,
  resetState,
  fetchDietDiaryMenuItems,
};

export const mapStateToProps = (state) => {
  return {
    totalItems: getTotalItems(state),
    entries: getEntries(state),
    searchedParams: getSearchedParams(state),
    canCreateEntriesMealLog: canCreateEntriesMealLog(state),
    canEditEntriesMealLog: canEditEntriesMealLog(state),
    canViewDetailMealLog: canViewDetailMealLog(state),
    loading: getLoading(state),
    dietMenuItems: getDietMenuItems(state),
  };
};
